import { useCallback, useEffect, useRef, useState } from 'react'
import { ServerApiClient, type TourShareOpenResponse } from '../../data/ServerApiClient'
import { markDemoSession, setDemoOwnerHome } from '../../data/demoSession'
import { buildCfoRoutePath, isCfoPageSlug } from '../parity-dev/cfo/cfoRoutes'
import { TOUR_QUERY_PARAM } from './tourSession'
import type { TourDoc } from './tourTypes'
import './tourShareEntry.css'

/**
 * `/t/<token>` — a personalized tour link.
 *
 * Resolves the link's gates and mints the demo session in one round trip, then
 * hands off to the real app with the tour and its audience already in the URL.
 * The alternative — `/t` redirecting to `/demo` redirecting again — is exactly
 * how the tour param got dropped before, and each extra hop is another chance
 * to lose it.
 *
 * The server never returns the document until every gate is satisfied, and it
 * reports *which* gate is unmet so this page can ask for precisely that.
 */
export function TourShareEntryPage() {
  const [state, setState] = useState<TourShareOpenResponse | null>(null)
  const [passcode, setPasscode] = useState('')
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const startedRef = useRef(false)

  const token = (() => {
    const parts = window.location.pathname.split('/').filter(Boolean)
    return parts[0] === 't' ? parts[1] || '' : ''
  })()

  const attempt = useCallback(async (creds?: { passcode?: string; email?: string }) => {
    setSubmitting(true)
    setError(null)
    const api = new ServerApiClient()
    try {
      const out = await api.openTourShareLink(token, creds)
      setState(out)
      if (out.status !== 'ok') {
        setSubmitting(false)
        // A wrong passcode returns the same gate as no passcode; say so rather
        // than silently redisplaying an empty form.
        if (creds?.passcode) setError('That passcode did not match.')
        else if (creds?.email) setError('That email is not allowed on this link.')
        return
      }

      const session = await api.demoSession()
      if (session?.status !== 'authenticated' || !session.access_token) {
        setSubmitting(false)
        setError('The live demo is not available right now.')
        return
      }
      markDemoSession(session.session_id)

      const doc = out.doc as TourDoc | undefined
      const page = doc?.entry?.route?.page
      const companies = await api.getDemoCompanies().catch(() => null)
      const wanted = doc?.entry?.company
      const picked =
        (wanted ? companies?.companies?.find((c) => c.industry_key === wanted) : null) ||
        companies?.companies?.[0] ||
        null

      const params = new URLSearchParams()
      if (out.tourSlug) params.set(TOUR_QUERY_PARAM, out.tourSlug)
      // Audience values ride as Storylane-compatible token params, so the same
      // link shape works whether it came from here or from a CRM sequence.
      Object.entries(out.audience || {}).forEach(([key, value]) => {
        if (value) params.set(`token[${key}]`, String(value))
      })

      const path = picked?.workbook_id
        ? buildCfoRoutePath({
            siteSlug: picked.site_slug || 'rivertown-advisory',
            workbookId: picked.workbook_id,
            page: isCfoPageSlug(page) ? page : 'ceo',
          })
        : '/rivertown-advisory/firm'
      setDemoOwnerHome(path)
      window.location.replace(`${path}?${params.toString()}`)
    } catch (err: unknown) {
      setSubmitting(false)
      const status = Number((err as { status?: number })?.status ?? NaN)
      setError(
        status === 404
          ? 'This link is no longer valid.'
          : status === 429
            ? 'Too many attempts — try again in a minute.'
            : 'Something went wrong opening this demo.',
      )
    }
  }, [token])

  useEffect(() => {
    if (startedRef.current || !token) return
    startedRef.current = true
    void attempt()
  }, [attempt, token])

  const status = state?.status
  const needsPasscode = status === 'passcode_required'
  const needsEmail = status === 'email_required'
  const dead = status === 'expired' || status === 'revoked'

  return (
    <div className="fg-tour-share">
      <div className="fg-tour-share__card">
        <div className="fg-tour-share__eyebrow">FuseGrid</div>

        {dead ? (
          <>
            <h1>This link has {status === 'expired' ? 'expired' : 'been turned off'}</h1>
            <p>Ask whoever sent it for a fresh one — or open the standing demo instead.</p>
            <a className="fg-tour-share__btn" href="/demo">Open the live demo</a>
          </>
        ) : needsPasscode || needsEmail ? (
          <>
            <h1>{state?.label ? `${state.label}` : 'One quick check'}</h1>
            <p>
              {needsPasscode
                ? 'This demo is passcode-protected. Enter the code you were sent.'
                : 'Enter your work email to open this demo.'}
            </p>
            {error ? <div className="fg-tour-share__error">{error}</div> : null}
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void attempt(needsPasscode ? { passcode, email: email || undefined } : { email, passcode: passcode || undefined })
              }}
            >
              {needsPasscode ? (
                <input
                  type="password"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  placeholder="Passcode"
                  autoComplete="one-time-code"
                  autoFocus
                  aria-label="Passcode"
                />
              ) : (
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  autoFocus
                  aria-label="Work email"
                />
              )}
              <button className="fg-tour-share__btn" type="submit" disabled={submitting}>
                {submitting ? 'Opening…' : 'Open the demo →'}
              </button>
            </form>
          </>
        ) : error ? (
          <>
            <h1>{error}</h1>
            <a className="fg-tour-share__btn" href="/signup?plan=pro">Start free on your own books</a>
          </>
        ) : (
          <>
            <h1>Opening your demo…</h1>
            <p>Loading a live walkthrough on real sample books.</p>
          </>
        )}
      </div>
    </div>
  )
}
